import MediaImage from "@/components/ui/MediaImage";
import { CaseData, CMSSection } from "@/cms/models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressCard } from "@fortawesome/sharp-light-svg-icons";
import VCard from "vcard-creator";
import Link from "next/link";
import { getPrimaryOrFirst } from "@/utils/helper";

interface AgentProps {
  caseData: CaseData;
  cmsSection: CMSSection<any>;
  contentPadding?: boolean;
}

const CaseAgent: React.FC<AgentProps> = ({
  caseData,
  cmsSection,
  contentPadding,
}) => {
  const useResourceGroupPhoneNumber =
    cmsSection.specifics.useResourceGroupPhoneNumber;
  const useResourceGroupEmail = cmsSection.specifics.useResourceGroupEmail;
  const showResourceGroupDisplayName =
    cmsSection.specifics.showResourceGroupDisplayName;

  const jobTitle = caseData.agent?.jobTitle ?? "";
  const displayName = caseData.agent?.displayName ?? "";
  const imageUrl = caseData.agent?.imageUrl ?? "";

  const phone =
    (useResourceGroupPhoneNumber
      ? caseData.resourceGroup?.phone
      : caseData?.agent?.phone) ?? "";
  const formattedPhone =
    (useResourceGroupPhoneNumber
      ? caseData.resourceGroup?.formattedPhone
      : caseData?.agent?.formattedPhone) ?? "";
  const email =
    (useResourceGroupEmail
      ? caseData?.resourceGroup?.email
      : caseData?.agent?.email) ?? "";

  const employeeImageAspectRatio =
    (cmsSection?.ui as any)?.employeeImageAspectRatio ??
    cmsSection?.specifics?.employeeImageAspectRatio;

  const website = getPrimaryOrFirst(
    (caseData.resourceGroup?.soMes ?? []).filter(
      (x) => x.namespace === "some.www",
    ),
  )?.url;

  return (
    <div
      className="homepage-text-sm homepage-card-rounded text-[var(--card-text-color)]"
      data-component-type="CaseAgent"
    >
      <div
        style={
          {
            "--aspect-ratio": employeeImageAspectRatio,
          } as React.CSSProperties
        }
        data-image={!!caseData?.agent?.imageUrl}
        className={`${employeeImageAspectRatio ? `aspect-[var(--aspect-ratio)]` : "h-80"} relative data-[image=true]:flex hidden`}
      >
        <MediaImage
          src={caseData.agent!.imageUrl!}
          alt="agent image"
          loading="eager"
          className="object-cover w-full h-full absolute"
        />
      </div>
      <div
        className={`flex flex-col gap-2 py-4 ${contentPadding ? "px-4" : ""}`}
      >
        <div className="flex gap-2 justify-between">
          <div>
            <div className="homepage-text-lg primary-font">{displayName}</div>
            <div>{jobTitle}</div>
          </div>
          <button
            className="flex"
            aria-label="vcard"
            onClick={() => {
              const vCard = new VCard();
              vCard.addName(displayName);
              if (jobTitle) {
                vCard.addRole(jobTitle);
              }
              if (imageUrl) {
                vCard.addPhotoURL(imageUrl);
              }
              if (email) {
                vCard.addEmail(email);
              }
              if (phone) {
                vCard.addPhoneNumber(phone, "CELL;VOICE");
              }

              const blob = new Blob([vCard.toString()], {
                type: "text/vcard",
              });

              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `${caseData.agent?.displayName}.vcf`;
              link.click();
              URL.revokeObjectURL(url);
            }}
          >
            <FontAwesomeIcon
              className="size-7 md:size-5"
              icon={faAddressCard}
            />
          </button>
        </div>
        <div>
          <Link aria-label="Phone" className="block" href={`tel:${phone}`}>
            {formattedPhone}
          </Link>
          <Link aria-label="Email" className="block" href={`mailto:${email}`}>
            {email}
          </Link>
        </div>
        {showResourceGroupDisplayName &&
          caseData.resourceGroup?.displayName &&
          (website ? (
            <Link href={website} className="homepage-text-md text-gray-500">
              {caseData.resourceGroup.displayName}
            </Link>
          ) : (
            <div className="homepage-text-md text-gray-500">
              {caseData.resourceGroup.displayName}
            </div>
          ))}
      </div>
    </div>
  );
};

export default CaseAgent;
